import { useMemo, useState } from 'react'

import {
  ChatTaggingRestControllerService,
  OfReleaseFormsService,
  type TaggableFriendsResult,
} from '@api'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from 'react-query'

import { useToast } from '@/components/ui/toast'
import { $creatorId } from '@/models/creators.ts'
import { listOrderOptions } from '@/modules/chatting/ui/chat-window/lists/consts'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import {
  taggableItemsFilterOptions,
  taggableItemsSortOptions,
} from '../constants'

export const useTagUserData = () => {
  const creatorId = useUnit($creatorId)!
  const queryClient = useQueryClient()
  const toast = useToast()
  const [query, setQuery] = useState('')
  const [order, setOrder] = useState(listOrderOptions[0].value)
  const [sort, setSort] = useState(taggableItemsSortOptions[0].value)
  const [filter, setFilter] = useState(taggableItemsFilterOptions[0].value)

  const queryKey = useMemo(
    () => ['taggable-users', creatorId, query, filter, sort, order],
    [creatorId, query, filter, sort, order],
  )

  const taggableUsersQuery = useInfiniteQuery(
    ['taggable-users', creatorId, query, filter, sort, order],
    ({ pageParam = 0 }) =>
      ChatTaggingRestControllerService.getTaggableFriends({
        creatorId,
        offset: pageParam,
        sort,
        order,
        filter,
        query,
      }),
    {
      getNextPageParam: (page, pages) => {
        const allItemsCount = pages.flatMap((page) => page && page.items).length

        return page && page.hasMore ? allItemsCount : undefined
      },
      refetchOnMount: true,
      enabled: !!creatorId,
      cacheTime: 0,
    },
  )

  const toggleShow = useMutation(
    ({ id, type }: { id: number; type: string; hide: boolean }) =>
      OfReleaseFormsService.toggleShow({
        creatorId,
        entityType: type,
        entityId: id,
      }),
    {
      onSuccess(_, { id, hide }) {
        queryClient.setQueriesData<
          InfiniteData<TaggableFriendsResult> | undefined
        >(queryKey, (taggableUsers) => {
          if (!taggableUsers) return taggableUsers

          return {
            ...taggableUsers,
            pages: taggableUsers.pages.map((page) => {
              const isPageToModify = page.items.find(
                (taggable) => taggable.id === id,
              )

              if (!isPageToModify) return page

              // It is possible to find hidden users using search
              if (!query && hide) {
                return {
                  ...page,
                  items: page.items.filter((taggable) => taggable.id !== id),
                }
              }

              const items = page.items.map((taggable) => {
                return taggable.id === id
                  ? {
                      ...taggable,
                      hiddenForRf: hide,
                    }
                  : taggable
              })

              return {
                ...page,
                items,
              }
            }),
          }
        })

        if (hide) {
          toast.success('Successfully removed from default list')
        } else {
          toast.success('Successfully back to default list')
        }
      },
      onError: (error, { hide }) => {
        handleError(
          error,
          hide
            ? 'Something went wrong while removing from default list'
            : 'Something went wrong while adding to default list',
        )
      },
    },
  )

  const taggableUsers = useMemo(
    () =>
      taggableUsersQuery.data?.pages
        .flatMap((page) => page && page.items)
        .filter(isTruthyAndNotEmpty) ?? [],
    [taggableUsersQuery.data?.pages],
  )

  const handleSearchChange = debounce<
    React.ChangeEventHandler<HTMLInputElement>
  >((event) => {
    const { value } = event.target

    if (!value.length || value.length > 1) {
      setQuery(value)
    }
  }, 500)

  const isLoading =
    taggableUsersQuery.isLoading && !taggableUsersQuery.isFetchingNextPage

  return {
    handleSearchChange,
    taggableUsers,
    isLoading,
    canLoadMore: Boolean(
      taggableUsersQuery.hasNextPage && !taggableUsersQuery.isFetchingNextPage,
    ),
    onLoadMore: taggableUsersQuery.fetchNextPage,
    isLoadingMore: taggableUsersQuery.isFetchingNextPage,
    toggleShow: toggleShow.mutate,
    filterSortOrderOptions: {
      sort,
      setSort,
      sortOptions: taggableItemsSortOptions,
      order,
      setOrder,
      orderOptions: listOrderOptions,
      filter,
      setFilter,
      filterOptions: taggableItemsFilterOptions,
    },
  }
}
